/**
 * rackReservation.logic.ts — Pure functional domain logic for Rack Space Reservations.
 *
 * Implements NetBox DCIM RackReservation patterns:
 * - Reserving unit ranges (u_start..u_end) for tenants/projects without creating fake equipment.
 * - Multi-segment space utilization metrics: Occupied (equipment) vs Reserved (hold) vs Available.
 * - Overlap detection between reservations and installed equipment with face & depth awareness.
 * - Defensive validation against NaN, Infinity, and non-integer unit indexing.
 */

export interface RackReservation {
  id: string;
  rack_id?: number | string;
  u_start: number;
  u_height: number;
  tenant: string;
  description: string;
  face?: 'front' | 'rear' | 'both';
  created_by?: string;
  created_at?: string;
}

export interface EquipmentSlotItem {
  id?: string;
  name?: string;
  u_position: number;
  u_height: number;
  is_zero_u?: boolean;
  mounting_position?: string;
}

export interface SpaceMetrics {
  totalU: number;
  occupiedU: number;
  reservedU: number;
  availableU: number;
  utilizationPct: number;
  occupiedPct: number;
  reservedPct: number;
  availablePct: number;
}

export interface ReservationValidationResult {
  valid: boolean;
  conflictReason?: string;
}

/**
 * Computes the 1-indexed inclusive unit range for a reservation.
 */
export function getReservationUnitRange(res: Pick<RackReservation, 'u_start' | 'u_height'>): { start: number; end: number } {
  const start = Math.floor(res.u_start);
  const height = Math.max(1, Math.floor(res.u_height));
  return {
    start,
    end: start + height - 1,
  };
}

/**
 * Returns an array of unit numbers held by the reservation (matches NetBox `units: int[]` schema).
 */
export function getReservationUnits(res: Pick<RackReservation, 'u_start' | 'u_height'>): number[] {
  const { start, end } = getReservationUnitRange(res);
  const units: number[] = [];
  for (let u = start; u <= end; u++) {
    units.push(u);
  }
  return units;
}

/**
 * Builds an O(1) unit-to-reservation lookup map for fast canvas slot rendering.
 */
export function buildReservationMap(reservations: RackReservation[]): Map<number, RackReservation> {
  const map = new Map<number, RackReservation>();
  for (const res of reservations) {
    const { start, end } = getReservationUnitRange(res);
    for (let u = start; u <= end; u++) {
      map.set(u, res);
    }
  }
  return map;
}

/**
 * Validates whether a reservation can be safely placed in a rack.
 *
 * Rules:
 * 1. Strict numeric validation: rejects NaN, Infinity, non-integers.
 * 2. Bounds check: 1 <= u_start, end <= totalU.
 * 3. Face & depth awareness: Half-depth Front reservations can coexist with Rear equipment.
 * 4. Overlap rejection: Cannot overlap with another reservation on conflicting faces.
 */
export function validateReservationPlacement(
  reservations: RackReservation[],
  equipmentList: EquipmentSlotItem[],
  uStart: number,
  uHeight: number,
  totalU: number,
  ignoreReservationId?: string,
  targetFace: 'front' | 'rear' | 'both' = 'both'
): ReservationValidationResult {
  // Defensive numeric checks
  if (!Number.isFinite(uStart) || !Number.isFinite(uHeight)) {
    return { valid: false, conflictReason: 'Параметры юнитов должны быть корректным числом' };
  }
  if (!Number.isInteger(uStart) || !Number.isInteger(uHeight)) {
    return { valid: false, conflictReason: 'Номер и высота юнитов должны быть целыми числами' };
  }
  if (uHeight < 1) {
    return { valid: false, conflictReason: 'Высота брони должна быть не менее 1U' };
  }

  const start = uStart;
  const end = start + uHeight - 1;

  if (start < 1 || end > totalU) {
    return {
      valid: false,
      conflictReason: `Диапазон U${start}..U${end} выходит за пределы стойки (${totalU}U)`,
    };
  }

  // Check collision with other reservations
  for (const res of reservations) {
    if (ignoreReservationId && res.id === ignoreReservationId) continue;
    const resRange = getReservationUnitRange(res);
    const resFace = res.face || 'both';

    const rangesOverlap = Math.max(start, resRange.start) <= Math.min(end, resRange.end);
    if (rangesOverlap) {
      const faceConflict =
        targetFace === 'both' ||
        resFace === 'both' ||
        targetFace === resFace;

      if (faceConflict) {
        return {
          valid: false,
          conflictReason: `Слоты U${start}..U${end} пересекаются с существующей бронью '${res.description || res.tenant}' (U${resRange.start}..U${resRange.end})`,
        };
      }
    }
  }

  // Check collision with installed physical equipment
  for (const eq of equipmentList) {
    if (eq.is_zero_u) continue;
    const eqStart = Math.floor(eq.u_position);
    const eqHeight = Math.max(1, Math.floor(eq.u_height || 1));
    const eqEnd = eqStart + eqHeight - 1;

    const rangesOverlap = Math.max(start, eqStart) <= Math.min(end, eqEnd);
    if (rangesOverlap) {
      const eqMount = (eq.mounting_position || 'Front').toLowerCase();
      const isEqFullDepth = eqMount.includes('full') || eqMount.includes('both');
      const isEqFront = eqMount.includes('front');
      const isEqRear = eqMount.includes('rear');

      let faceConflict = false;
      if (targetFace === 'both' || isEqFullDepth) {
        faceConflict = true;
      } else if (targetFace === 'front' && (isEqFront || isEqFullDepth)) {
        faceConflict = true;
      } else if (targetFace === 'rear' && (isEqRear || isEqFullDepth)) {
        faceConflict = true;
      }

      if (faceConflict) {
        return {
          valid: false,
          conflictReason: `Слоты U${start}..U${end} заняты оборудованием '${eq.name || 'Прибор'}' (U${eqStart}..U${eqEnd})`,
        };
      }
    }
  }

  return { valid: true };
}

/**
 * Calculates 3-way space distribution for rack elevation and capacity reporting:
 * - Occupied U: physically mounted equipment.
 * - Reserved U: unit reservations held for tenants/projects.
 * - Available U: unassigned, ready-to-mount units.
 */
export function calculateSpaceMetrics(
  totalU: number,
  equipmentList: EquipmentSlotItem[],
  reservations: RackReservation[]
): SpaceMetrics {
  const maxU = Math.max(0, Math.floor(totalU));
  const occupiedSlots = new Set<number>();
  const reservedSlots = new Set<number>();

  for (const eq of equipmentList) {
    if (eq.is_zero_u) continue;
    const start = Math.floor(eq.u_position);
    const height = Math.max(1, Math.floor(eq.u_height || 1));
    const end = Math.min(maxU, start + height - 1);
    for (let u = Math.max(1, start); u <= end; u++) {
      occupiedSlots.add(u);
    }
  }

  for (const res of reservations) {
    const { start, end } = getReservationUnitRange(res);
    const clampedEnd = Math.min(maxU, end);
    for (let u = Math.max(1, start); u <= clampedEnd; u++) {
      if (!occupiedSlots.has(u)) {
        reservedSlots.add(u);
      }
    }
  }

  const occupiedU = occupiedSlots.size;
  const reservedU = reservedSlots.size;
  const availableU = Math.max(0, maxU - occupiedU - reservedU);

  const utilizationPct = maxU > 0 ? Math.round(((occupiedU + reservedU) / maxU) * 100) : 0;
  const occupiedPct = maxU > 0 ? Math.round((occupiedU / maxU) * 100) : 0;
  const reservedPct = maxU > 0 ? Math.round((reservedU / maxU) * 100) : 0;
  const availablePct = Math.max(0, 100 - utilizationPct);

  return {
    totalU: maxU,
    occupiedU,
    reservedU,
    availableU,
    utilizationPct,
    occupiedPct,
    reservedPct,
    availablePct,
  };
}

/**
 * Finds contiguous blocks of free U slots in the rack.
 */
export function getFreeContiguousSlots(
  totalU: number,
  equipmentList: EquipmentSlotItem[],
  reservations: RackReservation[],
  includeReservedAsOccupied: boolean = true
): Array<{ start: number; size: number }> {
  const maxU = Math.max(0, Math.floor(totalU));
  const blocked = new Set<number>();

  for (const eq of equipmentList) {
    if (eq.is_zero_u) continue;
    const start = Math.floor(eq.u_position);
    const height = Math.max(1, Math.floor(eq.u_height || 1));
    const end = Math.min(maxU, start + height - 1);
    for (let u = Math.max(1, start); u <= end; u++) {
      blocked.add(u);
    }
  }

  if (includeReservedAsOccupied) {
    for (const res of reservations) {
      const { start, end } = getReservationUnitRange(res);
      const clampedEnd = Math.min(maxU, end);
      for (let u = Math.max(1, start); u <= clampedEnd; u++) {
        blocked.add(u);
      }
    }
  }

  const blocks: Array<{ start: number; size: number }> = [];
  let currentStart: number | null = null;

  for (let u = 1; u <= maxU; u++) {
    if (!blocked.has(u)) {
      if (currentStart === null) currentStart = u;
    } else if (currentStart !== null) {
      blocks.push({ start: currentStart, size: u - currentStart });
      currentStart = null;
    }
  }

  if (currentStart !== null) {
    blocks.push({ start: currentStart, size: maxU - currentStart + 1 });
  }

  return blocks;
}

/**
 * Adds a new reservation after validating placement and returns list sorted by u_start.
 */
export function addReservation(
  reservations: RackReservation[],
  newRes: RackReservation,
  equipmentList: EquipmentSlotItem[],
  totalU: number
): { reservations: RackReservation[]; error?: string } {
  const validation = validateReservationPlacement(
    reservations,
    equipmentList,
    newRes.u_start,
    newRes.u_height,
    totalU,
    undefined,
    newRes.face || 'both'
  );

  if (!validation.valid) {
    return { reservations, error: validation.conflictReason };
  }

  const updated = [...reservations, { ...newRes }].sort((a, b) => a.u_start - b.u_start);

  return {
    reservations: updated,
  };
}

/**
 * Removes a reservation by its ID.
 */
export function removeReservation(
  reservations: RackReservation[],
  reservationId: string
): RackReservation[] {
  return reservations.filter(r => r.id !== reservationId);
}
